import React, { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import { DataGrid } from "@mui/x-data-grid";
import router from "routes";
import { get, post } from "util/requests";
import colors from "colors";
import moment from "moment";
import styles from "./deviation_view.module.css";
import LoadingButton from "@mui/lab/LoadingButton";
import DangerousIcon from "@mui/icons-material/DangerousOutlined";
import PriorityHighIcon from "@mui/icons-material/PriorityHighRounded";
import { useLocation } from "react-router-dom";
import Tooltip from "@mui/material/Tooltip";
import DeviationsModal from "./components/deviations_modal";
import { renderFlagsRow } from "views/edc/components/crf/log_form_table";

export const deviations_columns = (permissions) => [
  {
    field: "status",
    headerName: "Status",
    width: 120,
    align: "center",
    headerAlign: "center",
  },
  {
    field: "modified_on",
    headerName: "Modified Date",
    width: 150,
    align: "center",
    headerAlign: "center",
    renderCell: ({ row }) => (row.created_on && moment(row.created_on).format("yyyy-MMM-DD HH:mm")) || "",
  },
  {
    field: "crf_name",
    headerName: "CRF",
    width: 240,
    align: "center",
    headerAlign: "center",
    renderCell: ({ row }) => row.crf_name || "-",
  },
  {
    field: "source",
    headerName: "Source",
    width: 100,
    align: "center",
    headerAlign: "center",
  },
  {
    field: "flags",
    headerName: "Flags",
    width: 150,
    align: "center",
    headerAlign: "center",
    renderCell: ({ row }) => {
      if (row.source === "auto") {
        return "-";
      } else {
        return renderFlagsRow(row, row.query, permissions);
      }
    },
  },
  {
    field: "question_text",
    headerName: "Question / Reason",
    width: 500,
    align: "left",
    headerAlign: "center",
    renderCell: ({ row }) => (row.source === "manual" ? row.data.deviation_reason : row.question_text),
  },
];

export function Table(props) {
  const { crf_id, subject_id, domain_id, updateCrfStatus, form_type, multi_entry_crf_id, data, setShowingModal } =
    props;

  const permissions = useSelector((state) => state.user.userData.permissions);

  return (
    <DataGrid
      rows={data}
      columns={deviations_columns(permissions)}
      disableColumnSelector
      disableRowSelectionOnClick
      disableColumnMenu
      pageSize={10}
      rowsPerPageOptions={[10]}
      hideFooterSelectedRowCount
      pageSizeOptions={[10]}
      columnHeaderHeight={34}
      rowHeight={34}
      initialState={{
        pagination: {
          paginationModel: {
            pageSize: 10,
          },
        },
      }}
      sx={{
        boxShadow: 0,
        border: 1,
        borderColor: "#f0f0f0",
        "& .MuiDataGrid-cell:hover": {
          color: "#8349fd",
          cursor: "pointer",
        },
        "& .MuiDataGrid-topContainer .MuiDataGrid-columnHeader ": {
          // backgroundColor: "#f7f3fe",
          backgroundColor: colors.table_header_background,
        },
        "& .MuiDataGrid-topContainer .MuiDataGrid-filler ": {
          // backgroundColor: "#f7f3fe",
          backgroundColor: colors.table_header_background,
        },
        "& .MuiDataGrid-columnHeaderTitle": {
          fontWeight: 700,
          color: "#333",
        },
      }}
      components={{
        NoRowsOverlay: () => <div className={styles.no_rows_overlay}>No Deviations or Violations</div>,
      }}
      onRowClick={(data) => {
        if (
          data.row.source === "manual" &&
          (permissions.includes("view_deviation_record") || permissions.includes("create_edit_deviation_record"))
        ) {
          setShowingModal({
            id: data.row.id,
            status: data.row.status,
            ...data.row.data,
            flags: data.row.flags,
            query: data.row.query,
          });
        } else {
          router.navigate(`${data.row.pathname}`);
        }
      }}
    />
  );
}

export default function DeviationsView(props) {
  const { crf_id, subject_id, domain_id, study_id } = props;
  const permissions = useSelector((state) => state.user.userData.permissions);
  const [data, setData] = useState([]);
  const [showingModal, setShowingModal] = useState(false);
  const location = useLocation();

  useEffect(() => {
    post("/footprint", {
      path: location.pathname,
      action: "pageload",
      name: "deviations_table",
      subject_id,
    });
  }, [location.pathname]);

  const fetch_table_data = () => get(`/deviations_table_view/${subject_id}`).then((ret) => setData(ret.data));
  useEffect(() => {
    fetch_table_data();
  }, [subject_id]);

  return (
    <div className={styles.wrapper}>
      <div>
        <div className={styles.stretch_header}>
          <div>
            <strong>Protocol Deviations</strong>
          </div>
          {permissions.includes("create_edit_deviation_record") ? (
            <div>
              <LoadingButton
                variant="contained"
                loading={false}
                onClick={() => setShowingModal(true)}
                sx={{
                  fontSize: 14,
                  padding: "2px",
                  paddingLeft: "10px",
                  paddingRight: "10px",
                }}
              >
                ADD DEVIATION
              </LoadingButton>
            </div>
          ) : null}
        </div>
      </div>

      <Table {...props} data={data} setShowingModal={setShowingModal} />
      <DeviationsModal
        subject_id={subject_id}
        showingModal={showingModal}
        setShowingModal={setShowingModal}
        RefetchTableData={fetch_table_data}
        study_id={study_id}
      />
    </div>
  );
}
